import {
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import Footer from "#app/components/footer";
import { LandingHeader } from "#app/components/landing/landing-header";
import { MarkdownContent } from "#app/components/markdown/MarkdownContent";
import { getRequestLocale } from "#app/i18n/get-request-locale.server";
import { loadLegalMarkdown } from "#app/lib/legal/load-legal-content.server";

export const meta: MetaFunction = () => [
  { title: "Terms of Service | Supervity AI" },
  {
    name: "description",
    content: "Read the terms and conditions for using Supervity AI services.",
  },
];

export const handle = { i18n: ["marketing", "footer", "common"] };

export async function loader({ request }: LoaderFunctionArgs) {
  const locale = await getRequestLocale(request);
  const content = await loadLegalMarkdown("tos", locale);
  return json({ content });
}

export default function TermsOfServiceRoute() {
  const { content } = useLoaderData<typeof loader>();
  const { t, i18n } = useTranslation("marketing");
  const dateStr = new Date().toLocaleDateString(
    i18n.language === "ja" ? "ja-JP" : "en-US",
    {
      year: "numeric",
      month: "long",
      day: "numeric",
    },
  );

  return (
    <div className="min-h-screen bg-[#FDFDFD] dark:bg-zinc-950">
      <LandingHeader backTo="/" backLabel={t("legal.backToHome")} />
      <div className="relative overflow-hidden bg-[url('/img/marketplace-bg.svg')] px-4 py-20 border-b border-[#E8EBF6] dark:border-zinc-800 dark:bg-zinc-950 dark:bg-none">
        <div className="absolute inset-0 hidden bg-zinc-950/80 dark:block" />
        <div className="absolute right-0 top-0 h-full w-[45%] bg-[radial-gradient(circle_at_center,rgba(128,149,212,0.24)_0%,rgba(128,149,212,0.08)_35%,rgba(255,255,255,0)_70%)] dark:bg-[radial-gradient(circle_at_center,rgba(128,149,212,0.18)_0%,rgba(24,24,27,0)_70%)]" />
        <div className="relative container mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#D8DDF0] bg-white/80 px-4 py-1.5 text-sm text-[#5C67AA] mb-6 shadow-sm dark:border-zinc-700 dark:bg-zinc-900/80 dark:text-indigo-300">
            <ShieldCheck
              size={16}
              className="text-[#535EA4] dark:text-indigo-300"
            />
            {t("legal.tosBadge")}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#141A42] dark:text-zinc-100 mb-6">
            {t("legal.tosTitle")}
          </h1>
          <p className="text-[#5C67AA] text-lg max-w-2xl mx-auto dark:text-indigo-300">
            {t("legal.lastUpdated", { date: dateStr })}
          </p>
        </div>
      </div>

      <div className="container mx-auto max-w-3xl px-4 py-8">
        <div className="bg-white rounded-2xl p-8 md:p-12 border border-[#E8EBF6] shadow-[0_4px_24px_rgba(83,94,164,0.04)] text-[#4A5568] dark:bg-zinc-900 dark:border-zinc-700 dark:text-zinc-300 dark:shadow-none">
          <MarkdownContent content={content} className="prose-lg" />
        </div>
      </div>
      <Footer />
    </div>
  );
}
